import type { OrganisationFilterDTO, FetchOrganisationResponse } from 'src/api/models';
import { OrganisationFilterDTOMatchMode } from 'src/api/models';
import { useOrganisationService } from 'src/services/organisation-service';
import type {
  EntitySearchConfig,
  EntitySearchResult,
  OrganisationEntity,
  PaginationParams,
  PaginatedResponse,
} from '../types';
import { ENTITY_ICONS, getEntityDisplayName, getEntityDisplayProps } from 'src/components/ticket-edit/entity-display';

export function createOrganisationSearchConfig(): EntitySearchConfig<OrganisationEntity> {
  const { fetchOrganisations, fetchOrganisation } = useOrganisationService();

  return {
    entityType: 'organisation',
    icon: ENTITY_ICONS.organisation,
    getDisplayName: (entity) => getEntityDisplayName(entity, 'organisation'),
    getDisplayProps: (entity) => getEntityDisplayProps(entity, 'organisation'),

    searchFields: [
      { name: 'name', weight: 2 },
      { name: 'number', weight: 2 },
      { name: 'vat_number', weight: 1 },
      { name: 'chamber_of_commerce_number', weight: 1 },
    ],

    async fetchAll(pagination: PaginationParams): Promise<PaginatedResponse<OrganisationEntity>> {
      const response = await fetchOrganisations({}, pagination) as any;
      const page = response.page ?? 1;
      const totalPages = response.total_pages ?? 1;
      return {
        data: response.data || [],
        hasMore: page < totalPages,
        totalPages,
      };
    },

    // When a unit/contact is selected, show their organisation as "related"
    async fetchByParent(
      relatedOrgId: string,
      _pagination: PaginationParams
    ): Promise<PaginatedResponse<OrganisationEntity>> {
      try {
        const org = await fetchOrganisation(relatedOrgId);
        return {
          data: org ? [org] : [],
          hasMore: false,
        };
      } catch {
        return { data: [], hasMore: false };
      }
    },

    async apiSearch(
      query: string,
      pagination: PaginationParams,
      excludeIds?: string[]
    ): Promise<PaginatedResponse<OrganisationEntity>> {
      const filters: OrganisationFilterDTO = {
        match_mode: OrganisationFilterDTOMatchMode.or,
        name: query,
        vat_number: query,
        chamber_of_commerce_number: query,
      };

      // Try to parse as organisation number (numeric)
      const orgNumber = parseInt(query, 10);
      if (!isNaN(orgNumber)) {
        filters.number = orgNumber;
      }

      const response = await fetchOrganisations(filters, pagination) as any;
      let data = response.data || [];
      const page = response.page ?? 1;
      const totalPages = response.total_pages ?? 1;

      // Exclude specified IDs
      if (excludeIds && excludeIds.length > 0) {
        const excludeSet = new Set(excludeIds);
        data = data.filter((org: OrganisationEntity) => org.id && !excludeSet.has(org.id));
      }

      return {
        data,
        hasMore: page < totalPages,
        totalPages,
      };
    },

    formatResult(org: OrganisationEntity, isRelated?: boolean): EntitySearchResult<OrganisationEntity> {
      const fullOrg = org as FetchOrganisationResponse;
      return {
        id: org.id || '',
        title: [org.name, org.number && `#${org.number}`].filter(Boolean).join(' '),
        subtitle: [org.description, org.type, fullOrg.address?.city].filter(Boolean).join(' - '),
        icon: 'pi-building',
        badges: [
          {
            text: org.active ? 'Active' : 'Inactive',
            variant: org.active ? 'success' : 'danger',
          },
        ],
        meta: {
          isRelated,
        },
        originalItem: org,
      };
    },

    // Organisation has no parent organisation
    getOrganisationId: undefined,
  };
}
